import { ApolloError, QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import Toggle from '../../../../common/src/components/Toggle'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import {
  AlignRight,
  BodyTextSpan,
  EllipsisDiv,
  ErrorText,
  H1,
  H3,
  H5,
  IntroText,
  Spacer,
} from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { TestIds } from '../../../../common/src/data/testids'
import {
  FetchTasksAndPropertiesQuery,
  FetchTasksAndPropertiesQueryVariables,
  JobName,
  TaskType,
} from '../../../../common/src/types.gen'
import { runAdminTaskUncaught } from '../../graphql/mutations'
import { fetchTasksAndPropertiesQuery } from '../../graphql/queries/queries'
import JobsHistorySection from '../components/JobsHistorySection'
import { TasksTable } from '../components/TasksTable'
import { Tab } from './BulkUpdatePage'

type LeasingTask = FetchTasksAndPropertiesQuery['firm']['tasks'][0]

interface MergeExistingTasksProps {
  firmId: number
  onUpdate: () => void
}

interface MergeExistingTasksImplProps {
  onUpdate: () => void
}

interface MergeExistingTasksState {
  selectedParentTask?: LeasingTask
  selectedTaskIds: Set<string | number>
  selectedTab: Tab
  errorParentTask: string
  errorChildTasks: string
  submitting: boolean
  jobStarted: boolean
  jobName: string
}

type MergeExistingTasksSectionImplProps = MergeExistingTasksImplProps &
  FetchTasksAndPropertiesQueryVariables &
  QueryResult<FetchTasksAndPropertiesQuery, FetchTasksAndPropertiesQueryVariables>

class MergeExistingTasksSectionImpl extends React.PureComponent<
  MergeExistingTasksSectionImplProps,
  MergeExistingTasksState
> {
  constructor(props: MergeExistingTasksSectionImplProps) {
    super(props)

    this.state = {
      selectedParentTask: undefined,
      selectedTaskIds: new Set<string | number>(),
      selectedTab: Tab.ACTIVE,
      errorParentTask: '',
      errorChildTasks: '',
      submitting: false,
      jobStarted: false,
      jobName: JobName.ADMIN_BULK_MERGE_TASKS,
    }
  }

  private tabOptions = [
    { key: Tab.ACTIVE, name: 'Active' },
    { key: Tab.ARCHIVED, name: 'Archived' },
  ]

  private taskLoad = (prefix: string) => {
    const tasks = this.props.data?.firm?.tasks
    const matchTasks = tasks
      ? tasks
          .filter(
            t =>
              t.taskType === TaskType.LEASING_PROPERTY && t.displayName.toLowerCase().startsWith(prefix.toLowerCase())
          )
          .slice(0, 10)
      : []

    return Promise.resolve(matchTasks)
  }

  private taskRow = (task: LeasingTask) => (
    <IntroText>
      <EllipsisDiv>
        <BodyTextSpan>{task.displayName}</BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )

  private onChooseParentTask = (selectedParentTask: LeasingTask) => {
    this.setState({ selectedParentTask })
  }

  private taskSelectionFunc = (task: LeasingTask) => task.displayName

  private onselectedTaskOrPropertyIdsChange = (selectedTaskIds: Set<string | number>) =>
    this.setState({ selectedTaskIds })

  private renderTasksTable = (isArchived: boolean, tasks: LeasingTask[]) => {
    if (tasks == null) {
      return null
    }

    const tasksToDisplay = tasks.filter(t => t.taskType === TaskType.LEASING_PROPERTY && t.isArchived === isArchived)

    return (
      <TasksTable
        tasks={tasksToDisplay}
        as="TableMultiselect"
        widths={['230px', null, null]}
        alignments={['left', 'right', 'right']}
        onChange={this.onselectedTaskOrPropertyIdsChange}
      />
    )
  }

  private tabToggleOnClick = (vals: string[]) => {
    this.setState({ selectedTab: Tab[vals[0] as keyof typeof Tab] })
  }

  private onMerge = () => {
    const { onUpdate } = this.props
    const { selectedParentTask, selectedTaskIds } = this.state

    this.setState({ submitting: true })

    let errorParentTask = ''
    let errorChildTasks = ''

    if (!selectedParentTask) {
      errorParentTask = 'must choose at least one parent task to update'
    }

    if (selectedTaskIds.size === 0) {
      errorChildTasks = 'must select at least one child task to merge into parent task'
    }

    if (selectedParentTask && selectedTaskIds.has(selectedParentTask.id)) {
      errorChildTasks = 'a child task cannot be the same as the parent task'
    }

    this.setState({ errorParentTask, errorChildTasks })
    if (errorParentTask || errorChildTasks) {
      this.setState({ submitting: false })
      return
    }

    // AdminService jobs accept the input args in the form of a String
    const input = this.state.selectedParentTask!.id.toString() + ',' + Array.from(this.state.selectedTaskIds).join(',')
    runAdminTaskUncaught({
      name: JobName.ADMIN_BULK_MERGE_TASKS,
      input,
      firmId: this.props.data?.firm.id,
    })
      .then(() => {
        onUpdate()
        this.setState({ submitting: false, jobStarted: true })
      })
      .catch((e: ApolloError) => {
        console.log(e)
        handleError(e)
        this.setState({ submitting: false })
      })
  }

  private renderMergeTaskSelectionForm = () => {
    const { data, firmId } = this.props
    const { selectedTab, submitting, errorChildTasks, errorParentTask, selectedParentTask } = this.state

    if (firmId == null) {
      return null
    }

    if (data == null || data.firm == null || data.firm.tasks == null) {
      return <div>data: {data}</div>
    }

    const filteredTasks = selectedParentTask
      ? data.firm.tasks.filter(t => t.id !== selectedParentTask.id)
      : data.firm.tasks

    return (
      <>
        <H5>What's the parent task?</H5>
        <Spacer height={16} />
        <TypeaheadBox<LeasingTask>
          loadFunc={this.taskLoad}
          rowFunc={this.taskRow}
          onChoose={this.onChooseParentTask}
          inputTextOnSelect={this.taskSelectionFunc}
          placeholder="Choose Task …"
          showResultsWhenEmpty
          clearOnBlur={false}
          hasError={errorParentTask !== ''}
        />
        {errorParentTask && <ErrorText data-testid={TestIds.errorText}>{errorParentTask}</ErrorText>}
        <Spacer height={64} />
        <H3>For which properties?</H3>
        <Spacer height={16} />
        <Toggle options={this.tabOptions} value={selectedTab} onSelect={this.tabToggleOnClick} />
        <Spacer height={16} />
        {selectedTab === Tab.ACTIVE
          ? this.renderTasksTable(false, filteredTasks)
          : this.renderTasksTable(true, filteredTasks)}

        {errorChildTasks && <ErrorText data-testid={TestIds.errorText}>{errorChildTasks}</ErrorText>}
        <Spacer height={32} />
        <AlignRight>
          <Button disabled={submitting} onClick={this.onMerge}>
            Merge Tasks
          </Button>
        </AlignRight>
        <Spacer width={16} />
      </>
    )
  }

  renderMergeTaskStarted = () => (
    <>
      <H1>Bulk update started</H1>
      <Spacer height={16} />
    </>
  )

  render() {
    const { jobStarted: jobStarted } = this.state

    return (
      <>
        {jobStarted ? this.renderMergeTaskStarted() : this.renderMergeTaskSelectionForm()}
        <Spacer height={16} />
        <JobsHistorySection jobKey={JSON.stringify({ firmId: this.props.data?.firm.id, type: 'admin' })} />
      </>
    )
  }
}

export default function MergeExistingTasksSection(props: MergeExistingTasksProps) {
  const { firmId } = props

  if (firmId == null) {
    return null
  }

  const res = useQuery<FetchTasksAndPropertiesQuery, FetchTasksAndPropertiesQueryVariables>(
    fetchTasksAndPropertiesQuery,
    {
      variables: { firmId },
    }
  )

  if (res.loading || res.error) {
    return null
  }

  return <MergeExistingTasksSectionImpl {...props} {...res} />
}
